import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';

// Components
import { URCodeFooter } from '../components/URCodeFooter';
import ConfirmModal from '../components/ConfirmModal';

// API & Utils
import { getAllOwnersAPI, clearAllOwnerProductsAPI } from '../api/bicycle';

// Types
import { AdminOwner } from '../types';

const AdminOwnersPage: React.FC = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [owners, setOwners] = useState<AdminOwner[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedOwner, setExpandedOwner] = useState<string | null>(null);
  
  // Modal state
  const [clearModal, setClearModal] = useState({
    isOpen: false,
    ownerId: '',
    ownerName: ''
  });

  useEffect(() => {
    loadAllOwners();
  }, []);

  const loadAllOwners = async () => {
    try {
      setLoading(true);
      const response = await getAllOwnersAPI();
      setOwners(response.owners);
    } catch (error) {
      console.error('Error loading owners:', error);
      setError('Failed to load owners list.');
    } finally {
      setLoading(false);
    }
  };

  const handleOwnerClick = (ownerId: string) => {
    setExpandedOwner(expandedOwner === ownerId ? null : ownerId);
  };

  const handleClearOwner = (owner: AdminOwner) => {
    setClearModal({
      isOpen: true,
      ownerId: owner.userId,
      ownerName: owner.user_name
    });
  };

  const confirmClearOwner = async () => {
    if (!clearModal.ownerId) return;

    try {
      await clearAllOwnerProductsAPI(clearModal.ownerId);
      // 삭제 후 목록 새로고침
      await loadAllOwners();
      setClearModal({ isOpen: false, ownerId: '', ownerName: '' });
    } catch (error) {
      console.error('Error clearing owner products:', error);
      alert('Failed to clear owner products. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingText>Loading...</LoadingText>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorText>{error}</ErrorText>
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>Admin - All Owners</Title>
          <BackButton onClick={() => navigate('/bicycle/owner-list')}>Back</BackButton>
        </Header>
        
        <InfoText>Total Owners: {owners.length}</InfoText>
        
        <OwnersList>
          {owners.map((owner) => (
            <OwnerCard key={owner.userId}>
              <OwnerHeader onClick={() => handleOwnerClick(owner.userId)}>
                <OwnerInfo>
                  <OwnerName>{owner.user_name}</OwnerName>
                  <OwnerDetails>
                    <DetailRow>
                      <DetailItem>📱 {owner.user_phone}</DetailItem>
                      <DetailItem>🚲 {owner.owned_ur.length} items</DetailItem>
                    </DetailRow>
                    <DetailItem>📅 {formatDate(owner.create_at)}</DetailItem>
                  </OwnerDetails>
                </OwnerInfo>
                <ExpandIcon expanded={expandedOwner === owner.userId}>
                  {expandedOwner === owner.userId ? '▼' : '▶'}
                </ExpandIcon>
              </OwnerHeader>
              
              {expandedOwner === owner.userId && (
                <OwnerContent>
                  <UrList>
                    {owner.owned_ur.length === 0 ? (
                      <EmptyUrText>No owned URCODEs</EmptyUrText>
                    ) : (
                      owner.owned_ur.map((item, index) => (
                        <UrItem key={index}>{item.ur}</UrItem>
                      ))
                    )}
                  </UrList>
                  {owner.owned_ur.length > 0 && (
                    <ClearButton onClick={() => handleClearOwner(owner)}>
                      Clear All URCODEs
                    </ClearButton>
                  )}
                </OwnerContent>
              )}
            </OwnerCard>
          ))}
        </OwnersList>
      </ContentWrapper>
      
      <URCodeFooter />
      
      {/* Clear Modal */}
      <ConfirmModal
        isOpen={clearModal.isOpen}
        title="Clear All URCODEs?"
        message={`Are you sure you want to clear all URCODEs for "${clearModal.ownerName}"? This action cannot be undone.`}
        confirmText="Clear All"
        onConfirm={confirmClearOwner}
        onCancel={() => setClearModal({ isOpen: false, ownerId: '', ownerName: '' })}
      />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #2595FF 0%, #69C8C9 100%);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ContentWrapper = styled.div`
  flex: 1;
  padding: 40px 16px;
  padding-bottom: 120px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 600;
  color: white;
  margin: 0;
`;

const BackButton = styled.button`
  background: transparent;
  color: white;
  border: 2px solid white;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const InfoText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
`;

const OwnersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OwnerCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const OwnerHeader = styled.div`
  padding: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s ease;
  gap: 12px;
  
  &:hover {
    background: #f8f8f8;
  }
`;

const OwnerInfo = styled.div`
  flex: 1;
  min-width: 0; // Allow text to shrink
`;

const OwnerName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 6px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const OwnerDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const DetailItem = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #666;
  white-space: nowrap;
`;

const ExpandIcon = styled.div<{ expanded: boolean }>`
  color: #666;
  font-size: 12px;
  transition: transform 0.2s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0)'};
  flex-shrink: 0;
`;

const OwnerContent = styled.div`
  padding: 0 14px 14px;
  border-top: 1px solid #f0f0f0;
`;

const UrList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const UrItem = styled.div`
  background: #f8f8f8;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  font-family: monospace;
  word-break: break-all;
`;

const EmptyUrText = styled.p`
  color: #999;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  padding: 16px 0;
  margin: 0;
`;

const ClearButton = styled.button`
  width: 100%;
  margin-top: 16px;
  padding: 12px 24px;
  background: #FF4757;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #FF3838;
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

const LoadingText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 50vh;
`;

const ErrorText = styled.p`
  color: white;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-top: 50vh;
`;

export default AdminOwnersPage;
